import type { IInfiniteQuery } from '@/main/api/use-infinite-query';
import { formQueryParamsString } from '@/main/sagas/utils/formQueryParamsString';
import type { IInputSearchItem } from '@components/common/InputSearch/types';
import {
  formatNumberToPhone,
  PhoneFormatType,
} from '@components/common/PhoneMask/formatNumberToPhone';
import type { IOptionItemProps } from '@components/common/Select/Select';

import { subscriberIdApi } from '../../store';
import { formatSubscriberIdApiUrl } from '../../utils/formatApiUrl';
import type {
  IManageNumberCreatePositionQuery,
  IManageNumberMeta,
  IManageNumberQuery,
  IManageNumberRedirection,
  IManageNumberRedirectionAddQuery,
  IManageNumberRedirectionQuery,
  IManageNumberRedirectionRemoveQuery,
  IManageNumberSimCard,
  IManageNumberSimCardUpdateQuery,
  IManageNumberSimCardUpdateResponse,
} from './types';

const manageNumberWithTags = subscriberIdApi.enhanceEndpoints({
  addTagTypes: [
    'ManageNumberMeta',
    'ManageNumberRedirection',
    'ManageNumberPositions',
  ],
});

export const manageNumberApi = manageNumberWithTags.injectEndpoints({
  endpoints: (builder) => ({
    getManageNumberMeta: builder.query<IManageNumberMeta, string>({
      query: (subscriberId) =>
        formatSubscriberIdApiUrl(subscriberId, 'manage-number/meta'),
      transformResponse: (response) => ({
        ...response,
        positionSelected: response?.positionSelected
          ? {
              label: response.positionSelected.name,
              value: response.positionSelected.id,
            }
          : undefined,
      }),
      providesTags: ['ManageNumberMeta'],
    }),

    getManageNumberPositionList: builder.query<
      IOptionItemProps[],
      IInfiniteQuery
    >({
      query: ({ searchValue, from }) => {
        const queryString = formQueryParamsString({
          from: from || 0,
          term: encodeURIComponent(searchValue || ''),
          size: 24,
        });
        return `positions${queryString}`;
      },
      providesTags: ['ManageNumberMeta', 'ManageNumberPositions'],
      transformResponse: (response: Array<{ name: string; id: string }>) =>
        response?.map((item) => ({ label: item.name, value: item.id })) || [],
    }),
    createManegeNumberPosition: builder.mutation<
      { id: number; name: string },
      IManageNumberCreatePositionQuery
    >({
      query: ({ name }) => ({
        url: `positions?name=${name}`,
        method: 'POST',
      }),
      invalidatesTags: (result) => {
        return result ? ['ManageNumberPositions'] : [];
      },
    }),
    updateManageNumberPosition: builder.mutation({
      query: ({
        subscriberId,
        positionId,
      }: {
        subscriberId: string;
        positionId: string;
      }) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'manage-number/position'),
        method: 'PUT',
        body: { id: positionId },
      }),
      invalidatesTags: ['ManageNumberMeta'],
    }),

    getManageNumberSimCard: builder.query<IManageNumberSimCard, string>({
      query: (subscriberId: string) =>
        formatSubscriberIdApiUrl(subscriberId, 'manage-number/sim-card'),
      providesTags: ['ManageNumberMeta'],
    }),
    updateManageNumberSimCard: builder.mutation<
      IManageNumberSimCardUpdateResponse,
      IManageNumberSimCardUpdateQuery
    >({
      query: ({ subscriberId, iccId, oldIccId, oldSimCardId }) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'manage-number/sim-card'),
        method: 'PUT',
        body: {
          icc: iccId,
          oldIcc: oldIccId,
          oldSimCardId,
        },
      }),
    }),
    getManageNumberSimCardSuggest: builder.query<
      IInputSearchItem[],
      IManageNumberQuery
    >({
      query: ({ subscriberId, searchValue, start }) => {
        const queryString = formQueryParamsString({
          from: start ? 0 : 0,
          term: encodeURIComponent(searchValue || ''),
          size: 24,
        });
        return formatSubscriberIdApiUrl(
          subscriberId,
          `manage-number/sim-card/search${queryString}`,
        );
      },
      transformResponse: (
        response: Array<{ simCardId: number; icc: number }>,
        _,
        arg,
      ) =>
        response?.map(({ simCardId, icc }) => ({
          id: simCardId.toString(),
          value: icc.toString(),
          match: arg.searchValue || '',
        })) ?? [],
    }),

    getManageNumberRedirection: builder.query<
      IManageNumberRedirection[],
      IManageNumberRedirectionQuery
    >({
      query: ({ subscriberId, force }) => {
        const query = formQueryParamsString({
          force: force ? 'true' : 'false',
        });
        return formatSubscriberIdApiUrl(
          subscriberId,
          `manage-number/forwarding/list${query}`,
        );
      },
      providesTags: ['ManageNumberMeta', 'ManageNumberRedirection'],
    }),
    addManageNumberRedirection: builder.mutation<
      void,
      IManageNumberRedirectionAddQuery
    >({
      query: ({ subscriberId, item }) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'manage-number/forwarding'),
        method: 'POST',
        body: {
          msisdn: formatNumberToPhone(item.phone, PhoneFormatType.msisdn),
          type: item.type.value,
          timeout: item.time?.value,
        },
      }),
      invalidatesTags: ['ManageNumberRedirection'],
    }),
    removeManageNumberRedirection: builder.mutation<
      void,
      IManageNumberRedirectionRemoveQuery
    >({
      query: ({ subscriberId, item }) => ({
        url: formatSubscriberIdApiUrl(subscriberId, 'manage-number/forwarding'),
        method: 'DELETE',
        body: {
          msisdn: formatNumberToPhone(item.msisdn, PhoneFormatType.msisdn),
          type: item.type,
          timeout: item.sec,
        },
      }),
      invalidatesTags: ['ManageNumberRedirection'],
    }),
  }),
});

export const {
  useGetManageNumberMetaQuery,

  useCreateManegeNumberPositionMutation,
  useUpdateManageNumberPositionMutation,

  useGetManageNumberSimCardQuery,
  useUpdateManageNumberSimCardMutation,

  useGetManageNumberRedirectionQuery,
  useAddManageNumberRedirectionMutation,
  useRemoveManageNumberRedirectionMutation,
} = manageNumberApi;
